import React, { useEffect, useMemo, useState } from 'react'
import { Box, Tab, Tabs } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { Container } from '../../components'
import { useUser } from '../../hooks'
import { User } from '../../models'
import { getPermissionRoleList, RootState } from '../../store'
import { useOrganizationManagement } from '../../hooks/useOrganizationManagement'
import OrganizationUsersRoot, {
    UserPageModalState,
} from './OrganizationUsers/OrganizationUsersRoot'
import { OrganizationVendorsRoot } from './OrganizationVendors/OrganizationVendorsRoot'

const USERS_TAB = 0
const VENDORS_TAB = 1

export const OrganizationWorkforceRoot = () => {
    const { organization } = useUser()

    const {
        loadingState,
        userList,
        vendorList,
        companyList,
        userDetail,
        userListActivityMap,
        userDetailActivityMap,
        getOrganizationUsers,
        getOrganizationVendors,
        getOrganizationCompanyList,
        getUserDetail,
        setUserDetail,
        bulkArchiveUser,
        setUserList,
        bulkAddUserToWorkspaces,
        getUserListActivity,
        getUserDetailActivity,
        setUserDetailActivityMap,
        markAsRegionalManager,
        markAsPropertyManager,
    } = useOrganizationManagement()

    const permissionRoleList = useSelector(
        (state: RootState) => state.permission.permissionRoleList,
    )

    const dispatch = useDispatch()
    const [selectedTab, setSelectedTab] = useState<number>(USERS_TAB)
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [
        showRegionalManagersOnly,
        setShowRegionalManagersOnly,
    ] = useState<boolean>(false)
    const [
        showPropertyManagersOnly,
        setShowPropertyManagersOnly,
    ] = useState<boolean>(false)
    const [modalState, setModalState] = useState<UserPageModalState>({
        removeUserWorkspacesModalOpen: false,
        addUserModalOpen: false,
        userActivityModalOpen: false,
        addNewUserModalOpen: false,
    })

    const filteredUserList = useMemo(() => {
        return userList.filter((user) => {
            const matchesSearch =
                user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                user.email.toLowerCase().includes(searchQuery.toLowerCase())
            const isRegionalManager = Boolean(user.regional_manager)
            const isPropertyManager = Boolean(user.property_manager)

            let matchesRoleFilter = true
            if (showRegionalManagersOnly && showPropertyManagersOnly) {
                matchesRoleFilter = isRegionalManager || isPropertyManager
            } else if (showRegionalManagersOnly) {
                matchesRoleFilter = isRegionalManager
            } else if (showPropertyManagersOnly) {
                matchesRoleFilter = isPropertyManager
            }

            return matchesSearch && matchesRoleFilter
        })
    }, [
        userList,
        searchQuery,
        showRegionalManagersOnly,
        showPropertyManagersOnly,
    ])

    useEffect(() => {
        if (organization) {
            getOrganizationUsers(organization)
            getOrganizationVendors(organization)
            getOrganizationCompanyList(organization)
            dispatch(getPermissionRoleList({ params: { organization } }))
            getUserListActivity(organization)
        }
    }, [organization])

    const onClickRemoveUserFromWorkspaces = (user: User) => {
        if (!organization) return
        setModalState((prevState) => ({
            ...prevState,
            removeUserWorkspacesModalOpen: true,
        }))
        getUserDetail(user.id, organization)
    }

    const handleCloseRemoveUserWorkspaces = () => {
        setModalState((prevState) => ({
            ...prevState,
            removeUserWorkspacesModalOpen: false,
        }))
        setUserDetail(null)
    }

    const onClickMarkAsRegionalManager = (user: User) => {
        if (!organization) return
        markAsRegionalManager(user.id, organization)
            .then(() => {
                toast.success('User marked as regional manager')
            })
            .catch((error: any) => {
                toast.error(error.response.data.message)
            })
    }

    const onClickMarkAsPropertyManager = (user: User, companyId: number) => {
        if (!organization) return
        markAsPropertyManager(user.id, organization, companyId)
            .then(() => {
                toast.success('User marked as property manager')
            })
            .catch((error: any) => {
                toast.error(error.response.data.message)
            })
    }

    const onClickAddUser = (user: User) => {
        if (!organization) return
        setModalState((prevState) => ({
            ...prevState,
            addUserModalOpen: true,
        }))
        getUserDetail(user.id, organization)
    }

    const handleCloseAddUser = () => {
        setModalState((prevState) => ({
            ...prevState,
            addUserModalOpen: false,
        }))
        setUserDetail(null)
    }

    const onClickUserActivity = (user: User) => {
        if (!organization) return
        setModalState((prevState) => ({
            ...prevState,
            userActivityModalOpen: true,
        }))
        getUserDetail(user.id, organization)
        getUserDetailActivity(user.id, organization)
    }

    const handleCloseUserActivity = () => {
        setModalState((prevState) => ({
            ...prevState,
            userActivityModalOpen: false,
        }))
        setUserDetailActivityMap({})
        setUserDetail(null)
    }

    const onClickAddNewUser = () => {
        setModalState((prevState) => ({
            ...prevState,
            addNewUserModalOpen: true,
        }))
    }

    const handleCloseAddNewUser = () => {
        setModalState((prevState) => ({
            ...prevState,
            addNewUserModalOpen: false,
        }))
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                width: '100%',
                overflow: 'hidden',
                padding: 0,
                maxWidth: 'none',
            }}
        >
            <Box
                style={{
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    marginBottom: selectedTab === VENDORS_TAB ? 0 : 24,
                }}
            >
                <Tabs
                    value={selectedTab}
                    onChange={(_, newValue: number) => setSelectedTab(newValue)}
                >
                    <Tab
                        label="Org Users"
                        style={{
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 700,
                        }}
                    />
                    <Tab
                        label="Org Vendors"
                        style={{
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 700,
                        }}
                    />
                </Tabs>
            </Box>
            {selectedTab === USERS_TAB && (
                <OrganizationUsersRoot
                    loadingState={loadingState}
                    filteredUserList={filteredUserList}
                    userListActivityMap={userListActivityMap}
                    userDetail={userDetail}
                    userDetailActivityMap={userDetailActivityMap}
                    organization={organization}
                    permissionRoleList={permissionRoleList}
                    companyList={companyList}
                    searchQuery={searchQuery}
                    showRegionalManagersOnly={showRegionalManagersOnly}
                    showPropertyManagersOnly={showPropertyManagersOnly}
                    modalState={modalState}
                    setSearchQuery={setSearchQuery}
                    onToggleRegionalManagersOnly={() =>
                        setShowRegionalManagersOnly((prev) => !prev)
                    }
                    onTogglePropertyManagersOnly={() =>
                        setShowPropertyManagersOnly((prev) => !prev)
                    }
                    onClickAddNewUser={onClickAddNewUser}
                    onClickRemoveUserFromWorkspaces={
                        onClickRemoveUserFromWorkspaces
                    }
                    onClickAddUser={onClickAddUser}
                    onClickUserActivity={onClickUserActivity}
                    onClickMarkAsRegionalManager={onClickMarkAsRegionalManager}
                    onClickMarkAsPropertyManager={onClickMarkAsPropertyManager}
                    handleCloseRemoveUserWorkspaces={
                        handleCloseRemoveUserWorkspaces
                    }
                    handleCloseAddUser={handleCloseAddUser}
                    handleCloseUserActivity={handleCloseUserActivity}
                    handleCloseAddNewUser={handleCloseAddNewUser}
                    bulkArchiveUser={bulkArchiveUser}
                    setUserList={setUserList}
                    bulkAddUserToWorkspaces={bulkAddUserToWorkspaces}
                />
            )}
            {selectedTab === VENDORS_TAB && (
                <OrganizationVendorsRoot
                    vendorList={vendorList}
                    allProperties={companyList}
                    isLoading={loadingState.vendorList}
                    organizationId={organization}
                />
            )}
        </Container>
    )
}
